import { combineLatest, timer } from 'rxjs';

import { Injectable, inject } from '@angular/core';

import {
  BaseWsService,
  ParsedWebsocketMessage,
  ResourcesService,
  ToastService,
  WebsocketMessageService,
} from '@mitsubishi/core';

import { StudyEventDto } from '../../models/study-next.model';
import { ConnectedUsersFacade } from '../../state/connected-users/connected-users.facade';
import { ConnectedUser } from '../../state/connected-users/connected-users.model';
import { ProjectStudiesStore } from '../../stores/project-studies.store';
import { isNewStudyPageEnabled } from '../../utils/new-study-page';
import { PROJECT_TOPIC, ProjectMessages } from './project.messages';

@Injectable({
  providedIn: 'root',
})
export class ProjectWsService extends BaseWsService {
  /**
   * The new study page keeps `ProjectStudiesStore` current from the study events, which reach the whole
   * project area on this topic; the legacy page keeps its studies in the NGXS tree, fed by the JSON diff.
   */
  readonly #studiesOutsideTree = isNewStudyPageEnabled();
  readonly #projectStudies = inject(ProjectStudiesStore);

  constructor(
    wsMessageService: WebsocketMessageService,
    serverResourceService: ResourcesService,
    toastService: ToastService,
    private connectedUsersFacade: ConnectedUsersFacade
  ) {
    super(wsMessageService, serverResourceService, toastService);
  }

  subscribeTopic(projectId: string) {
    super.subscribeTopic(projectId);

    this.connectCurrentUserToProject();
    this.checkConnectedUsers();
  }

  protected get excludedMessagesFromLog(): string[] {
    return [ProjectMessages.UserConnected];
  }

  protected setTopic(projectId: string) {
    this.topic = `${PROJECT_TOPIC}/${projectId}`;
  }

  protected handleMessage(message: ParsedWebsocketMessage) {
    super.handleMessage(message);

    const payload = message.payload;
    switch (message.description) {
      case ProjectMessages.LogMessage: {
        this.logMessage(payload.message);
        break;
      }
      case ProjectMessages.StudyEventMessage: {
        if (this.#studiesOutsideTree) {
          this.#projectStudies.onStudyEvent(payload as StudyEventDto);
        }
        break;
      }
      case ProjectMessages.UserConnected: {
        const userToConnect: ConnectedUser = payload;
        userToConnect.lastConnected = +new Date();
        this.connectedUsersFacade.connectUserToProject(userToConnect);
        break;
      }
    }
  }

  private connectCurrentUserToProject(): void {
    this.subs.sink = combineLatest([timer(0, 5000), this.authFacade.userInfo$]).subscribe(([, userInfo]) => {
      if (userInfo != null) {
        const user: ConnectedUser = {
          id: userInfo.username,
          username: userInfo.username,
        };
        this.wsMessageService.sendMessage(this.topic, ProjectMessages.UserConnected, user);
      }
    });
  }

  private checkConnectedUsers(): void {
    const disconnectTimeout = 10000;

    this.subs.sink = combineLatest([timer(0, 5000), this.connectedUsersFacade.connectedUsers$]).subscribe(
      ([, connectedUsers]) => {
        connectedUsers.forEach(user => {
          if (user.lastConnected != null && +new Date() - user.lastConnected > disconnectTimeout) {
            this.connectedUsersFacade.disconnectUserFromProject(user.id);
          }
        });
      }
    );
  }
}
